import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useTranslations } from 'hooks/useTranslations';
import { EPermission } from 'models/permissions';
import { Link } from 'react-router-dom';
import { getLegalEntityType } from 'components/organisms/RegisteredFirearmDetails';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { ERouteLinks } from 'models/route';
import { IAccountResponse } from 'models/account';
import { ELegalVisibility } from 'constants/LegalVisibility';
import { renderDataColor } from '../../../components/molecules/Table/helpers';
import { Chips } from '../../../components/atoms/Chips';

interface IWeaponTableCard {
  data: ITableDataSource;
  // eslint-disable-next-line react/no-unused-prop-types
  licenceId: string;
  // eslint-disable-next-line react/no-unused-prop-types
  leId: string;
  // eslint-disable-next-line react/no-unused-prop-types
  propsLink: string;
  state?: any;
  hasViewAllFirearmsPermission: boolean;
  account?: IAccountResponse;
}

export const WeaponTableCard = ({
  data,
  hasViewAllFirearmsPermission,
  account,
}: IWeaponTableCard) => {
  const dataColor = renderDataColor(data);
  const { t } = useTranslations();
  return (
    <Grid
      container
      sx={{
        border: `1px solid ${dataColor.border}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        ml: '4px',
        backgroundColor: dataColor.background,
      }}
      columnSpacing={2}
    >
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.weapon.fireCycleId.column')}
        </Typography>
        <Typography variant="body2">{data.fireCycleId}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.weapon.type.column')}
        </Typography>
        <Typography variant="body2">{data.type}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.weapon.make.column')}
        </Typography>
        <Typography variant="body2">{data.make}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.weapon.model.column')}
        </Typography>
        <Typography variant="body2">{data.model}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.weapon.calibre.column')}
        </Typography>
        <Typography variant="body2">{data.calibre}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.weapon.action.column')}
        </Typography>
        <Typography variant="body2">{data.action}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.weapon.state.column')}
        </Typography>
        <Chips text={data.state} variant={dataColor.stateField} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.weapon.serialNumber.column')}
        </Typography>
        <Typography variant="body2">{data.serialNumber}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.weapon.owner.column')}
        </Typography>
        <StyledLegalLink
          legalId={data.ownerUniqueId}
          legalType={getLegalEntityType(data.ownerType)}
          labelText={data.owner}
          legalVisibilityUniqueId={data.ownerVisibilityUniqueId}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.weapon.keeper.column')}
        </Typography>
        <StyledLegalLink
          legalId={data.keeperUniqueId}
          legalType={getLegalEntityType(data.keeperType)}
          labelText={data.keeper}
          legalVisibilityUniqueId={data.keeperVisibilityUniqueId}
        />
      </Grid>

      <Grid
        container
        size={{ xs: 12 }}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.primary100}`,
            lg: 'none',
          },
          px: 2,
          pb: 2,
          mt: 1,
          pt: 2,
        }}
      >
        <Grid size={{ xs: 12, sm: 6 }} sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('general.actions.text')}:{' '}
          </Typography>
        </Grid>
        <Grid
          size={{ xs: 12, sm: 6 }}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            '& a': { width: { xs: '100%', sm: 'auto' } },
          }}
        >
          <Link
            to={ERouteLinks.ViewRegisteredFirearm.replace(
              ':id',
              data.firearmUniqueId as string,
            )}
          >
            {account &&
              hasViewAllFirearmsPermission &&
              (!data.ownerPrefixGroupUniqueId ||
                account.prefixGroupIds.includes(data.ownerPrefixGroupUniqueId)) &&
              (!data.keeperPrefixGroupUniqueId ||
                account.prefixGroupIds.includes(data.keeperPrefixGroupUniqueId)) && (
                <Button
                  label={t('legalEntity.licenceDetails.weapon.view.column')}
                  variant={EButtonVariants.outlined}
                  size={EButtonSizes.small}
                  permission={[EPermission.VIEW_ALL_FIREARMS, EPermission.VIEW_FIREARMS]}
                  permissionOr
                  fullWidth
                />
              )}
            {account &&
              !hasViewAllFirearmsPermission &&
              (!data.ownerVisibilityUniqueId ||
                data.ownerVisibilityUniqueId === ELegalVisibility.PUBLIC ||
                account.assignedLegalIds.includes(data.ownerUniqueId)) &&
              (!data.ownerPrefixGroupUniqueId ||
                account.prefixGroupIds.includes(data.ownerPrefixGroupUniqueId)) &&
              (!data.keeperVisibilityUniqueId ||
                data.keeperVisibilityUniqueId === ELegalVisibility.PUBLIC ||
                account.assignedLegalIds.includes(data.keeperUniqueId)) &&
              (!data.keeperPrefixGroupUniqueId ||
                account.prefixGroupIds.includes(data.keeperPrefixGroupUniqueId)) && (
                <Button
                  label={t('legalEntity.licenceDetails.weapon.view.column')}
                  variant={EButtonVariants.outlined}
                  size={EButtonSizes.small}
                  permission={[EPermission.VIEW_ALL_FIREARMS, EPermission.VIEW_FIREARMS]}
                  permissionOr
                  fullWidth
                />
              )}
          </Link>
        </Grid>
      </Grid>
    </Grid>
  );
};
